import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Trophy, Loader2, Eye, EyeOff, KeyRound, Chrome } from 'lucide-react';
import { ThemeToggle } from '../components/ThemeToggle';
import { useTheme } from '../contexts/ThemeContext';

export default function LoginPage({ embutido = false, aoIrParaCadastro = null }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [failedAttempts, setFailedAttempts] = useState(0);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      await login(email, password);
      navigate('/dashboard');
    } catch (err) {
      setFailedAttempts(prev => prev + 1);
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  /* ETAPA VISUAL 7 — `flex-col` + `justify-center` no lugar de `items-center`
     num flex de LINHA: era o padrão que corta o topo quando o conteúdo passa da
     janela (o ícone do logo aparecia pela metade). O padding vertical subiu de
     16px (`p-4`) para 32/40px (`py-8 sm:py-10`), e o bloco do logo foi
     compactado para a tela caber COM esse respiro. Se ainda assim o conteúdo
     passar da janela, o `min-h-screen` deixa o container crescer e a página rola
     mantendo o padding nas duas pontas. */
  /* ETAPA 11 — `embutido`: a MESMA tela, sem a moldura de página (sem
     `min-h-screen`, sem o bloco do logo, sem o botão de tema). É assim que ela
     entra na landing, ao lado do hero. TODA a lógica abaixo (estado, chamadas de
     API, Google, tratamento de erro) é idêntica — o que muda é só o que ENVOLVE
     o cartão. Por isso a tela de login continua sendo uma só. */
  return (
    <div className={embutido ? '' : 'min-h-screen bg-[#0A0A0A] flex flex-col items-center justify-center px-4 py-6 sm:py-8'}>
      {!embutido && (
        <div className="absolute top-4 right-4">
          <ThemeToggle variant="icon" />
        </div>
      )}
      <div className={embutido ? '' : 'w-full max-w-md'}>
        {!embutido && (
          <div className="text-center mb-4">
            <div className="inline-flex items-center justify-center w-12 h-12 rounded-2xl bg-gradient-to-br from-[#D4AF37] to-[#B8860B] mb-2">
              <Trophy className="w-6 h-6 text-black" />
            </div>
            <h1 className="text-2xl font-bold text-white font-['Chivo']">BJJ Manager</h1>
          </div>
        )}

        {/* Login Form */}
        <div className="bg-[#141414] border border-[#27272A] rounded-xl p-5">
          <h2 className="text-lg font-bold text-white mb-3 font-['Chivo']">Entrar</h2>
          
          {error && (
            <div className="mb-4 p-3 rounded-lg bg-red-500/10 border border-red-500/30 text-red-400 text-sm" data-testid="login-error">
              {error}
            </div>
          )}

          {failedAttempts >= 1 && (
            <div className="mb-4 p-3 rounded-lg bg-[#D4AF37]/10 border border-[#D4AF37]/30" data-testid="forgot-password-prompt">
              <div className="flex items-center gap-2 mb-1">
                <KeyRound size={16} className="text-[#D4AF37]" />
                <span className="text-[#D4AF37] font-medium text-sm">Esqueceu sua senha?</span>
              </div>
              <p className="text-[#A1A1AA] text-xs mb-2">Recupere o acesso a sua conta em poucos passos.</p>
              <Link
                to="/forgot-password"
                className="inline-block text-sm font-bold text-[#D4AF37] hover:text-[#E5C158] underline underline-offset-2"
                data-testid="forgot-password-link"
              >
                Recuperar senha
              </Link>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-3">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-[#A1A1AA]">Email</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="seu@email.com"
                required
                data-testid="login-email"
                className="bg-[#1A1A1A] border-[#27272A] text-white placeholder:text-[#71717A] focus:border-[#D4AF37] focus:ring-[#D4AF37]"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-[#A1A1AA]">Senha</Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="********"
                  required
                  data-testid="login-password"
                  className="bg-[#1A1A1A] border-[#27272A] text-white placeholder:text-[#71717A] focus:border-[#D4AF37] focus:ring-[#D4AF37] pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[#71717A] hover:text-white"
                >
                  {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>

            <Button
              type="submit"
              disabled={isLoading}
              data-testid="login-submit"
              className="w-full bg-[#D4AF37] text-black hover:bg-[#E5C158] font-bold py-3"
            >
              {isLoading ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                'Entrar'
              )}
            </Button>
          </form>

          {/* Login com Google — OAuth 2.0 nativo (sem dependência da Emergent) */}
          <GoogleLoginButton />

          <div className="mt-4 text-center">
            <p className="text-[#71717A]">
              Nao tem conta?{' '}
              {/* Embutido, o "Cadastre-se" troca o painel para o cadastro (sem
                  sair da landing); na página dedicada segue sendo um link. */}
              {aoIrParaCadastro ? (
                <button type="button" onClick={aoIrParaCadastro} className="text-[#D4AF37] hover:text-[#E5C158]" data-testid="register-link">
                  Cadastre-se
                </button>
              ) : (
                <Link to="/register" className="text-[#D4AF37] hover:text-[#E5C158]" data-testid="register-link">
                  Cadastre-se
                </Link>
              )}
            </p>
          </div>

          <div className="mt-2 text-center">
            <Link to="/public" className="text-[#A1A1AA] hover:text-white text-sm" data-testid="public-link">
              Ver resultados publicos →
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Google OAuth 2.0 login button.
 * Calls GET /api/auth/google/prepare to get a CSRF state + auth URL,
 * then redirects the browser to Google.
 * The backend handles the callback at /api/auth/google/callback.
 */
function GoogleLoginButton() {
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState('');
  const { theme } = useTheme();
  const isLight = theme === 'light';

  // Check for Google auth error in URL params
  React.useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const googleError = params.get('google_error');
    if (googleError) {
      const messages = {
        access_denied: 'Acesso negado pelo Google.',
        missing_params: 'Parâmetros ausentes na resposta do Google.',
        invalid_state: 'Sessão inválida. Tente novamente.',
        token_exchange_failed: 'Falha ao validar com o Google. Tente novamente.',
        no_id_token: 'Resposta inválida do Google.',
        invalid_id_token: 'Token do Google inválido.',
        no_email: 'Email não encontrado na conta Google.',
        email_not_verified: 'Email não verificado no Google.',
      };
      setError(messages[googleError] || 'Erro ao entrar com Google.');
      // Clean URL params
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  const handleGoogleLogin = async () => {
    setLoading(true);
    setError('');
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL || '';
      const { data } = await axios.get(`${API_URL}/api/auth/google/prepare`);
      // Redirect to Google OAuth authorization URL
      window.location.href = data.auth_url;
    } catch (err) {
      console.error('Failed to init Google OAuth:', err);
      setError('Não foi possível iniciar o login com Google.');
      setLoading(false);
    }
  };

  return (
    <div className="mt-3">
      {error && (
        <div className="mb-3 p-3 rounded-lg bg-red-500/10 border border-red-500/30 text-red-400 text-sm" data-testid="google-login-error">
          {error}
        </div>
      )}
      <Button
        type="button"
        onClick={handleGoogleLogin}
        disabled={loading}
        data-testid="google-login-btn"
        className={`w-full font-bold py-3 transition-colors ${
          isLight
            ? 'bg-white text-[#111827] border border-[#E5E7EB] hover:bg-gray-50'
            : 'bg-transparent text-white border border-[#374151] hover:bg-white/5'
        }`}
      >
        {loading ? (
          <Loader2 className="w-5 h-5 animate-spin" />
        ) : (
          <svg className="w-5 h-5" viewBox="0 0 24 24">
            <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
            <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
            <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
            <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
          </svg>
        )}
        Entrar com Google
      </Button>
    </div>
  );
}
